import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

/**
 * Tooltip on a Radix portal.
 *
 * The portal is the point: the collapsed sidebar rail clips its own overflow,
 * so the hand-rolled tooltip it used to render — absolutely positioned at
 * `left: 100%` inside that rail — was cut down to about five visible pixels and
 * read as a rendering artefact rather than a label. Rendering into the body
 * escapes the clip. That matters more than it sounds: collapsed, the label is
 * the only thing telling two identical icons apart.
 */
const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef(({ className, sideOffset = 8, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-[1100] overflow-hidden rounded-md border border-border bg-popover",
        "px-2.5 py-1.5 text-xs font-medium text-popover-foreground shadow-md",
        "data-[state=delayed-open]:animate-fade-in",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/** The whole thing in one call, for the common "label this control" case. */
const Tooltip = ({ label, side = "right", delayDuration = 250, children }) => {
  if (!label) return children;
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipRoot>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side}>{label}</TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
};

export { Tooltip, TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent };
